import { AsyncPipe } from '@angular/common';
import { Component, Input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
} from '@angular/material/table';
import { catchError, map, Observable, switchMap, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { Group } from '../../../../core/models/group.interface';
import { LayoutService } from '../../../../core/services/layout/layout';
import { GroupService } from '../../../../core/services/group/group';
import { PAGINATION } from '../../../../core/constants/constants';
import { Filter, QueryGet } from '../../../../core/types/query-types';
import { GroupForm } from '../group-form/group-form';
import { PermisionDirective } from '../../../../shared/directives/permision.directive';
import { NgxPaginationModule } from 'ngx-pagination';
import { LoadingService } from '../../../../core/services/loading/loading';
import { Search } from "../../../../shared/components/search/search";

@Component({
  selector: 'app-group-list',
  imports: [
    PermisionDirective,
    MatIcon,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    NgxPaginationModule,
    AsyncPipe,
    Search
],
  templateUrl: './group-list.html',
  styleUrl: './group-list.css',
})
export class GroupList {
  groups: Group[] = [];
  public page = 1;
  asyncGrupos!: Observable<Group[]>;
  @Input() orden: string = '';
  limit = 10;
  total = 0;
  showColumns: string[] = ['acciones', 'nombre', 'Privilegios', 'permisos'];

  constructor(
    private dialog: MatDialog,
    private groupService: GroupService,
    private layoutService: LayoutService,
    private router: Router,
    private toastr: ToastrService,
    private activatedRouter: ActivatedRoute,
    private title: Title,
    private loadingService: LoadingService,
  ) {
    this.title.setTitle('Grupos');
  }

  ngOnInit(): void {
    this.layoutService.paginationLimit$
      .pipe(
        switchMap((limit) => {
          this.limit = limit;
          return this.activatedRouter.queryParamMap;
        }),
        switchMap((params) => {
          const pageNum = params.get('page') || '1';
          const search = params.get('search') || '';
          this.page = parseInt(pageNum);
          this.fetchGroups(PAGINATION.first_page, null, search);
          return this.layoutService.paginationLimit$;
        }),
      )
      .subscribe();
  }

  fetchGroups(page: number, filter: Filter | null = null, search = '') {
    const offset = page > 1 ? page * this.limit - this.limit : 0;
    const params: QueryGet = {
      order: '-id',
      limit: this.limit,
      offset,
      search,
      filters: [],
    };
    if (filter) {
      params.filters.push(filter);
    }
    this.page = page;
    this.loadingService.show(true);
    this.asyncGrupos = this.groupService.getGroups(params).pipe(
      tap((data) => {
        this.groups = data.results;
        this.groupService.setGrupo(data.results[0]);
        this.groupService.setGrupos(data.results);
        this.total = data.count;
      }),
      map((res) => {
        this.loadingService.show(false);
        return res.results;
      }),
      catchError((error) => {
        this.loadingService.show(false);
        const errorMsg = `¡Se ha producido un error, inténtelo nuevamente!`;
        this.toastr.error(errorMsg, 'Error!', {
          progressBar: true,
          timeOut: 5000,
        });
        this.router.navigate(['home']);
        return [];
      }),
    );
  }

  openForm(group: Group | null) {
    const dialogRef = this.dialog.open(GroupForm, {
      width: '600px',
      height: 'auto',
      maxHeight: '80vh',
      disableClose: true,
      data: { group: group },
    });

    dialogRef.afterClosed().subscribe(() => {
      this.fetchGroups(1);
    });
  }

  onRevokePermision(group: Group) {
    const confirmMsg = `Esta acción revocará todos los permisos al grupo ${group.name} ¿Estas seguro?`;
    const isConfirmed = confirm(confirmMsg);
    if (!isConfirmed) return;
    this.groupService.revokePermision(group).subscribe({
      next: () => {
        this.toastr.success(
          `Se revocaron todos los permisos del grupo ${group.name}, correctamente!`,
          'Exitoso!',
        );
        this.fetchGroups(1);
      },
      error: (err) => {
        this.toastr.error(`No pudimos revocar los permisos del grupo ${group.name}.`, 'Error!');
      },
    });
  }

  generatePermisions() {
    return this.groupService.generatePermisions();
  }
}
